import React, { useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { recurringCostCandidatesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function RecurringCostDetectionPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [detecting, setDetecting] = useState(false);

  const fetchDiagnostics = async () => {
    setLoading(true);
    try {
      const { data: result } = await recurringCostCandidatesAPI.diagnostics();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runDetection = async () => {
    setDetecting(true);
    try {
      await recurringCostCandidatesAPI.runDetection();
      toast.success('Detection complete');
      await fetchDiagnostics();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDetecting(false);
    }
  };

  const stats = data?.entity_stats || [];
  const totalNew = stats.reduce((s, e) => s + (e.new || 0), 0);
  const totalPromoted = stats.reduce((s, e) => s + (e.promoted || 0), 0);
  const totalDismissed = stats.reduce((s, e) => s + (e.dismissed || 0), 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Recurring Cost Detection</CardTitle>
            <CardDescription>
              Per-entity coverage: bank transaction clusters detected as recurring property costs
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runDetection}
              disabled={detecting}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-run-detection-btn"
            >
              {detecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Run Detection
            </button>
            <button
              onClick={fetchDiagnostics}
              disabled={loading}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Load'}
            </button>
          </div>
        </div>
      </CardHeader>
      {data && (
        <CardContent>
          <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
            <span>Last run: {data.last_run || 'Never'}</span>
            <span>Entities: {(data.detection_scope_entities || data.property_owning_entities || []).length}</span>
            <Badge variant="outline" className="text-[10px]">{totalNew} new</Badge>
            <Badge className="bg-green-100 text-green-700 text-[10px]">{totalPromoted} promoted</Badge>
            <Badge className="bg-muted text-muted-foreground text-[10px]">{totalDismissed} dismissed</Badge>
          </div>
          {stats.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entity</TableHead>
                  <TableHead className="text-right">New</TableHead>
                  <TableHead className="text-right">Promoted</TableHead>
                  <TableHead className="text-right">Dismissed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.map((e) => (
                  <TableRow key={e.entity_id}>
                    <TableCell className="text-xs">{e.entity_name}</TableCell>
                    <TableCell className="text-right text-xs font-mono">{e.new || 0}</TableCell>
                    <TableCell className="text-right text-xs font-mono">{e.promoted || 0}</TableCell>
                    <TableCell className="text-right text-xs font-mono">{e.dismissed || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      )}
    </Card>
  );
}
